// Dashboard công việc: lọc theo kỳ / phòng ban / dự án, mọi con số bấm vào được để xem danh sách việc tương ứng
import React, { useMemo, useState } from 'react';
import { Button, DatePicker, Drawer, Empty, Segmented, Select, Table, Tag, Tooltip } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { useNavigate } from 'react-router-dom';
import dayjs, { type Dayjs } from 'dayjs';
import * as XLSX from 'xlsx';
import { Bar, CartesianGrid, Cell, ComposedChart, Legend, Line, Pie, PieChart, ResponsiveContainer, Tooltip as RTooltip, XAxis, YAxis } from 'recharts';
import { AlertTriangle, CheckCircle2, ClipboardList, Download, Hourglass, RotateCcw, UserX } from 'lucide-react';
import Layout from '../../Layout';
import { DEPARTMENTS, PROJECTS, REPEAT_LABEL, departmentName, memberOf, projectOf, type WorkTask } from '../WorkList/data';
import useWorkStore from '../WorkList/store';
import {
  BUCKETS, BUCKET_META, QUADRANT_META, bucketOf, countOf, doneCount, filterBase, filterTasks, groupTasks, isOpen, openCount, pct,
  quadrantOf, routineCompliance, tasksOfGroup, today, topPerformers, trend, type Bucket, type Filters, type GroupBy, type GroupRow, type Quadrant, type RoutineRow,
} from './logic';

const fmt = (d?: string) => (d ? dayjs(d).format('DD/MM/YYYY') : '—');
const num = (n: number) => n.toLocaleString('vi-VN');

const defaultFilters = (): Filters => ({
  from: dayjs().subtract(29, 'day').format('YYYY-MM-DD'),
  to: today(),
  basis: 'created',
  scope: 'all',
});

const quarterStart = () => dayjs().month(Math.floor(dayjs().month() / 3) * 3).startOf('month');
const RANGE_PRESETS: { label: string; value: [Dayjs, Dayjs] }[] = [
  { label: '7 ngày qua', value: [dayjs().subtract(6, 'day'), dayjs()] },
  { label: '30 ngày qua', value: [dayjs().subtract(29, 'day'), dayjs()] },
  { label: 'Tháng này', value: [dayjs().startOf('month'), dayjs()] },
  { label: 'Tháng trước', value: [dayjs().subtract(1, 'month').startOf('month'), dayjs().subtract(1, 'month').endOf('month')] },
  { label: 'Quý này', value: [quarterStart(), dayjs()] },
  { label: '90 ngày qua', value: [dayjs().subtract(89, 'day'), dayjs()] },
];

function exportExcel(fileName: string, rows: Record<string, string | number>[]) {
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(rows), 'Bao cao');
  XLSX.writeFile(wb, fileName);
}

const Card = ({ title, extra, className = '', children }: { title?: React.ReactNode; extra?: React.ReactNode; className?: string; children: React.ReactNode }) => (
  <div className={`flex flex-col rounded-xl border border-gray-200 bg-white p-4 shadow-sm dark:border-gray-700 dark:bg-gray-800 ${className}`}>
    {(title || extra) && (
      <div className="mb-3 flex items-center justify-between gap-2">
        <h3 className="text-[14px] font-bold text-gray-800 dark:text-gray-200">{title}</h3>
        {extra}
      </div>
    )}
    {children}
  </div>
);

const Avatar = ({ name, color, size = 28 }: { name: string; color?: string; size?: number }) => (
  <span className="inline-flex shrink-0 items-center justify-center rounded-full text-[11px] font-bold text-white" style={{ width: size, height: size, background: color || '#94a3b8' }}>
    {name.split(' ').pop()?.[0] ?? '?'}
  </span>
);

const BucketTag = ({ b }: { b: Bucket }) => <Tag color={BUCKET_META[b].color} className="!m-0">{BUCKET_META[b].label}</Tag>;

function Kpi({ icon, label, value, sub, tone, onClick }: { icon: React.ReactNode; label: string; value: string; sub: React.ReactNode; tone: string; onClick: () => void }) {
  return (
    <button onClick={onClick} className="group flex h-full flex-col rounded-xl border border-gray-200 bg-white p-4 text-left shadow-sm transition hover:border-[#3e91ab] hover:shadow dark:border-gray-700 dark:bg-gray-800">
      <span className="flex items-center gap-2 text-[13px] font-semibold text-gray-600 dark:text-gray-300">
        <span className={`flex h-7 w-7 items-center justify-center rounded-lg ${tone}`}>{icon}</span>{label}
      </span>
      <span className="mt-2 text-3xl font-light leading-none text-slate-800 dark:text-white">{value}</span>
      <span className="mt-1.5 text-xs text-gray-500 dark:text-gray-400">{sub}</span>
    </button>
  );
}

/** Thanh tỉ lệ các nhóm trạng thái */
const StackBar = ({ row }: { row: GroupRow }) => (
  <Tooltip title={BUCKETS.filter(b => row[b]).map(b => `${BUCKET_META[b].short}: ${row[b]}`).join(' · ')}>
    <div className="flex h-2 w-full min-w-[90px] overflow-hidden rounded bg-gray-100 dark:bg-gray-700">
      {BUCKETS.map(b => (row[b] ? <div key={b} style={{ width: `${(row[b] / row.total) * 100}%`, background: BUCKET_META[b].color }} /> : null))}
    </div>
  </Tooltip>
);

type DrawerState = { title: string; tasks: WorkTask[] } | null;

function TaskDrawer({ state, onClose }: { state: DrawerState; onClose: () => void }) {
  const navigate = useNavigate();
  const [bucket, setBucket] = useState<Bucket | 'all'>('all');
  const now = today();
  const tasks = state?.tasks ?? [];
  const shown = bucket === 'all' ? tasks : tasks.filter(t => bucketOf(t, now) === bucket);
  const present = BUCKETS.filter(b => tasks.some(t => bucketOf(t, now) === b));
  const columns: ColumnsType<WorkTask> = [
    { key: 'code', title: 'Mã', width: 90, render: (_, t) => <span className="font-mono text-xs font-semibold">{t.code}</span> },
    {
      key: 'name', title: 'Công việc', render: (_, t) => (
        <span><b>{t.name}</b>{t.parentId && <Tag className="!ml-1.5 !text-[10px]">Việc con</Tag>}<div className="text-xs text-slate-500">{projectOf(t.projectId)?.name}</div></span>
      ),
    },
    { key: 'assignee', title: 'Người làm', width: 170, render: (_, t) => { const m = memberOf(t.assigneeId); return m ? m.name : <span className="text-red-500">Chưa giao</span>; } },
    {
      key: 'due', title: 'Hạn chót', width: 110, sorter: (a, b) => (a.dueAt ?? '9').localeCompare(b.dueAt ?? '9'),
      render: (_, t) => {
        const b = bucketOf(t, now);
        const late = b === 'overdue' && t.dueAt ? dayjs(now).diff(t.dueAt, 'day') : 0;
        return <span>{fmt(t.dueAt)}{late > 0 && <div className="text-xs text-red-500">trễ {late} ngày</div>}</span>;
      },
    },
    { key: 'status', title: 'Trạng thái', width: 170, render: (_, t) => <BucketTag b={bucketOf(t, now)} /> },
  ];
  const doExport = () => exportExcel(`cong-viec-${now}.xlsx`, shown.map(t => ({
    'Mã': t.code, 'Công việc': t.name, 'Dự án / phòng ban': projectOf(t.projectId)?.name ?? '', 'Phòng ban': departmentName(t.departmentId),
    'Người tạo': memberOf(t.creatorId)?.name ?? '', 'Người làm': memberOf(t.assigneeId)?.name ?? 'Chưa giao',
    'Ngày tạo': fmt(t.createdAt), 'Hạn chót': fmt(t.dueAt), 'Ngày xong': fmt(t.doneAt), 'Trạng thái': BUCKET_META[bucketOf(t, now)].label,
  })));
  return (
    <Drawer open={!!state} onClose={onClose} width={860} title={<span>{state?.title} <span className="font-normal text-slate-500">({tasks.length})</span></span>}
      afterOpenChange={o => { if (!o) setBucket('all'); }}
      extra={<span className="flex gap-2"><Button icon={<Download size={14} />} onClick={doExport} disabled={!shown.length}>Xuất Excel</Button><Button type="primary" onClick={() => navigate('/work-list')}>Mở danh sách công việc</Button></span>}>
      {present.length > 1 && (
        <Segmented className="mb-3" value={bucket} onChange={v => setBucket(v as Bucket | 'all')}
          options={[{ value: 'all', label: `Tất cả (${tasks.length})` }, ...present.map(b => ({ value: b, label: `${BUCKET_META[b].short} (${tasks.filter(t => bucketOf(t, now) === b).length})` }))]} />
      )}
      <Table<WorkTask> rowKey="id" size="small" columns={columns} dataSource={shown} pagination={{ pageSize: 15, hideOnSinglePage: true, showSizeChanger: false }}
        onRow={t => ({ onClick: () => navigate(`/work-list?task=${t.id}`), className: 'cursor-pointer' })} />
      <p className="mt-2 text-xs text-slate-500">Bấm vào một dòng để mở chi tiết công việc.</p>
    </Drawer>
  );
}

function GroupTable({ rows, tasks, by, onOpen }: { rows: GroupRow[]; tasks: WorkTask[]; by: GroupBy; onOpen: (title: string, list: WorkTask[]) => void }) {
  const open = (r: GroupRow, b?: Bucket) => {
    const list = tasksOfGroup(tasks, by, r.key);
    onOpen(b ? `${r.name} · ${BUCKET_META[b].label}` : r.name, b ? list.filter(t => bucketOf(t) === b) : list);
  };
  const numCol = (b: Bucket): ColumnsType<GroupRow>[number] => ({
    key: b, align: 'right', width: 92, sorter: (x, y) => x[b] - y[b],
    title: <Tooltip title={BUCKET_META[b].hint}><span className="whitespace-nowrap">{BUCKET_META[b].short}</span></Tooltip>,
    render: (_, r) => (r[b]
      ? <button onClick={e => { e.stopPropagation(); open(r, b); }} className="font-semibold hover:underline" style={{ color: BUCKET_META[b].color }}>{r[b]}</button>
      : <span className="text-gray-300 dark:text-gray-600">0</span>),
  });
  const columns: ColumnsType<GroupRow> = [
    {
      key: 'name', title: by === 'project' ? 'Dự án / phòng ban' : by === 'department' ? 'Phòng ban' : 'Thành viên', width: 260, fixed: 'left',
      render: (_, r) => (
        <span className="flex items-center gap-2.5">
          {(by === 'assignee' || by === 'creator') && <Avatar name={r.name} color={r.color} />}
          <span className="min-w-0"><b className="block truncate">{r.name}</b>{r.sub && <span className="text-xs text-slate-500">{r.sub}</span>}</span>
        </span>
      ),
    },
    { key: 'bar', title: 'Tỉ lệ', width: 140, render: (_, r) => <StackBar row={r} /> },
    { key: 'total', title: 'Tổng', align: 'right', width: 80, sorter: (x, y) => x.total - y.total, defaultSortOrder: 'descend', render: (_, r) => <b>{r.total}</b> },
    ...BUCKETS.map(numCol),
    ...(by === 'creator' ? [{ key: 'unassigned', title: <Tooltip title="Việc đang mở chưa giao cho ai"><span>Chưa giao</span></Tooltip>, align: 'right' as const, width: 92, sorter: (x: GroupRow, y: GroupRow) => x.unassigned - y.unassigned, render: (_: unknown, r: GroupRow) => (r.unassigned ? <b className="text-red-500">{r.unassigned}</b> : <span className="text-gray-300">0</span>) }] : []),
    {
      key: 'rate', title: <Tooltip title="Đúng hạn / số việc đã hoàn thành"><span className="whitespace-nowrap">% đúng hạn</span></Tooltip>, align: 'right', width: 100,
      sorter: (x, y) => pct(x.on_time, doneCount(x)) - pct(y.on_time, doneCount(y)),
      render: (_, r) => (doneCount(r) ? `${pct(r.on_time, doneCount(r))}%` : '—'),
    },
  ];
  return (
    <Table<GroupRow> rowKey="key" size="middle" columns={columns} dataSource={rows} scroll={{ x: 'max-content' }} pagination={{ pageSize: 8, hideOnSinglePage: true, showSizeChanger: false }}
      onRow={r => ({ onClick: () => open(r), className: 'cursor-pointer' })} locale={{ emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Không có công việc trong kỳ" /> }} />
  );
}

const groupExport = (rows: GroupRow[], by: GroupBy) => rows.map(r => ({
  [by === 'project' ? 'Dự án / phòng ban' : by === 'department' ? 'Phòng ban' : 'Thành viên']: r.name,
  'Tổng': r.total,
  ...Object.fromEntries(BUCKETS.map(b => [BUCKET_META[b].label, r[b]])),
  ...(by === 'creator' ? { 'Chưa giao': r.unassigned } : {}),
  '% đúng hạn': doneCount(r) ? pct(r.on_time, doneCount(r)) : '',
}));

export default function WorkReport() {
  const [f, setF] = useState<Filters>(defaultFilters);
  const [projBy, setProjBy] = useState<'project' | 'department'>('project');
  const [memberBy, setMemberBy] = useState<'assignee' | 'creator'>('assignee');
  const [drawer, setDrawer] = useState<DrawerState>(null);
  const all = useWorkStore(s => s.tasks);
  const navigate = useNavigate();
  const set = (p: Partial<Filters>) => setF(prev => ({ ...prev, ...p }));
  const now = today();

  const tasks = useMemo(() => filterTasks(all, f, now), [all, f, now]);
  const c = useMemo(() => countOf(tasks, now), [tasks, now]);
  const series = useMemo(() => trend(filterBase(all, f, now), f.from, f.to), [all, f, now]);
  const projRows = useMemo(() => groupTasks(tasks, projBy, now), [tasks, projBy, now]);
  const memberRows = useMemo(() => groupTasks(tasks, memberBy, now), [tasks, memberBy, now]);
  const top = useMemo(() => topPerformers(tasks, 5, now), [tasks, now]);
  const routines = useMemo(() => routineCompliance(tasks, now), [tasks, now]);
  const openTasks = tasks.filter(t => isOpen(bucketOf(t, now)));
  const quadrants = (Object.keys(QUADRANT_META) as Quadrant[]).map(q => ({ q, list: openTasks.filter(t => quadrantOf(t) === q) }));
  const longestOverdue = tasks.filter(t => bucketOf(t, now) === 'overdue').sort((a, b) => (a.dueAt ?? '').localeCompare(b.dueAt ?? '')).slice(0, 5);

  const openDrawer = (title: string, list: WorkTask[]) => setDrawer({ title, tasks: list });
  const byBucket = (b: Bucket) => openDrawer(BUCKET_META[b].label, tasks.filter(t => bucketOf(t, now) === b));
  const pie = BUCKETS.map(b => ({ b, name: BUCKET_META[b].label, value: c[b] })).filter(x => x.value);
  const isDefault = JSON.stringify(f) === JSON.stringify(defaultFilters());
  const projectOptions = PROJECTS.filter(p => !f.departmentId || p.departmentId === f.departmentId).map(p => ({ value: p.id, label: p.name }));

  return (
    <Layout>
      <div className="mx-auto w-full max-w-[1800px] space-y-4 pb-10">
        <div className="flex flex-wrap items-end justify-between gap-2">
          <div>
            <h1 className="text-xl font-bold text-gray-800 dark:text-gray-100">Dashboard công việc</h1>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              {f.basis === 'created' ? 'Việc tạo' : 'Việc có hạn chót'} từ {fmt(f.from)} đến {fmt(f.to)} · bấm vào số bất kỳ để xem danh sách
            </p>
          </div>
        </div>

        {/* Bộ lọc */}
        <div className="flex flex-wrap items-center gap-2 rounded-xl border border-gray-200 bg-white p-2.5 shadow-sm dark:border-gray-700 dark:bg-gray-800">
          <DatePicker.RangePicker allowClear={false} format="DD/MM/YYYY" presets={RANGE_PRESETS} value={[dayjs(f.from), dayjs(f.to)]}
            onChange={v => v && v[0] && v[1] && set({ from: v[0].format('YYYY-MM-DD'), to: v[1].format('YYYY-MM-DD') })} />
          <Segmented value={f.basis} onChange={v => set({ basis: v as Filters['basis'] })} options={[{ value: 'created', label: 'Theo ngày tạo' }, { value: 'due', label: 'Theo hạn chót' }]} />
          <Select className="w-52" allowClear placeholder="Tất cả phòng ban" value={f.departmentId} options={DEPARTMENTS.map(d => ({ value: d.id, label: d.name }))}
            onChange={v => set({ departmentId: v, projectId: v && f.projectId && projectOf(f.projectId)?.departmentId !== v ? undefined : f.projectId })} />
          <Select className="w-60" allowClear showSearch optionFilterProp="label" placeholder="Tất cả dự án" value={f.projectId} options={projectOptions} onChange={v => set({ projectId: v })} />
          <Select className="w-52" value={f.scope} onChange={v => set({ scope: v })} options={[{ value: 'all', label: 'Công việc & việc con' }, { value: 'main', label: 'Chỉ công việc chính' }]} />
          <Select className="w-48" allowClear placeholder="Tất cả trạng thái" value={f.bucket} onChange={v => set({ bucket: v })} options={BUCKETS.map(b => ({ value: b, label: BUCKET_META[b].label }))} />
          {!isDefault && <Button type="text" icon={<RotateCcw size={14} />} onClick={() => setF(defaultFilters())}>Đặt lại</Button>}
        </div>

        {/* KPI */}
        <div className="grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-5">
          <Kpi icon={<ClipboardList size={16} />} tone="bg-sky-50 text-sky-600" label="Công việc" value={num(c.total)}
            sub={<>{num(openCount(c))} đang mở · {num(doneCount(c))} hoàn thành</>} onClick={() => openDrawer('Tất cả công việc', tasks)} />
          <Kpi icon={<CheckCircle2 size={16} />} tone="bg-green-50 text-green-600" label="Tỷ lệ đúng hạn" value={doneCount(c) ? `${pct(c.on_time, doneCount(c))}%` : '—'}
            sub={<>{num(c.on_time)} đúng hạn / {num(doneCount(c))} đã xong</>} onClick={() => byBucket('on_time')} />
          <Kpi icon={<AlertTriangle size={16} />} tone="bg-red-50 text-red-600" label="Quá hạn" value={num(c.overdue)}
            sub={<span className={c.overdue ? 'text-red-500' : ''}>{pct(c.overdue, openCount(c))}% số việc đang mở</span>} onClick={() => byBucket('overdue')} />
          <Kpi icon={<Hourglass size={16} />} tone="bg-purple-50 text-purple-600" label="Chờ duyệt" value={num(c.review)}
            sub={c.reviewOverdue ? <span className="text-red-500">{c.reviewOverdue} việc đã quá hạn</span> : 'Chưa có việc trễ duyệt'} onClick={() => byBucket('review')} />
          <Kpi icon={<UserX size={16} />} tone="bg-amber-50 text-amber-600" label="Chưa giao" value={num(c.unassigned)}
            sub="Việc đang mở chưa có người làm" onClick={() => openDrawer('Chưa giao người làm', openTasks.filter(t => !t.assigneeId))} />
        </div>

        {/* Xu hướng + trạng thái */}
        <div className="grid grid-cols-1 gap-4 xl:grid-cols-3">
          <Card className="xl:col-span-2" title="Tiến độ trong kỳ"
            extra={<span className="text-xs text-gray-400">{series.length > 0 && series[0].label.includes('–') ? 'Theo tuần' : 'Theo ngày'}</span>}>
            <div className="h-[280px]">
              <ResponsiveContainer>
                <ComposedChart data={series} margin={{ top: 5, right: 8, left: -12, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e5e7eb" />
                  <XAxis dataKey="label" tick={{ fontSize: 11 }} interval="preserveStartEnd" minTickGap={12} />
                  <YAxis yAxisId="l" tick={{ fontSize: 11 }} allowDecimals={false} />
                  <YAxis yAxisId="r" orientation="right" tick={{ fontSize: 11 }} allowDecimals={false} />
                  <RTooltip />
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                  <Bar yAxisId="l" dataKey="created" name="Tạo mới" fill="#94a3b8" radius={[3, 3, 0, 0]} maxBarSize={18} />
                  <Bar yAxisId="l" dataKey="done" name="Hoàn thành" fill="#22c55e" radius={[3, 3, 0, 0]} maxBarSize={18} />
                  <Line yAxisId="r" dataKey="open" name="Còn tồn (trục phải)" stroke="#3e91ab" strokeWidth={2} dot={false} />
                </ComposedChart>
              </ResponsiveContainer>
            </div>
          </Card>
          <Card title="Trạng thái công việc">
            {c.total === 0 ? <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Không có công việc" /> : (
              <>
                <div className="relative mx-auto h-[180px] w-[180px]">
                  <ResponsiveContainer>
                    <PieChart>
                      <Pie data={pie} dataKey="value" innerRadius={62} outerRadius={84} paddingAngle={1} stroke="none" onClick={d => byBucket((d.payload as { b: Bucket }).b)} className="cursor-pointer">
                        {pie.map(x => <Cell key={x.b} fill={BUCKET_META[x.b].color} />)}
                      </Pie>
                      <RTooltip />
                    </PieChart>
                  </ResponsiveContainer>
                  <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center">
                    <span className="text-3xl font-light text-slate-800 dark:text-white">{num(c.total)}</span>
                    <span className="text-[10px] font-bold uppercase tracking-wider text-gray-400">Công việc</span>
                  </div>
                </div>
                <div className="mt-3 grid grid-cols-2 gap-1">
                  {BUCKETS.map(b => (
                    <button key={b} onClick={() => byBucket(b)} disabled={!c[b]} className="flex items-center justify-between rounded px-2 py-1 text-xs hover:bg-gray-50 disabled:opacity-40 dark:hover:bg-gray-700">
                      <span className="flex items-center gap-1.5 text-gray-600 dark:text-gray-300"><span className="h-2.5 w-2.5 rounded-full" style={{ background: BUCKET_META[b].color }} />{BUCKET_META[b].short}</span>
                      <b className="text-gray-800 dark:text-gray-100">{c[b]}</b>
                    </button>
                  ))}
                </div>
                {c.noDue > 0 && <div className="mt-2 border-t border-gray-100 pt-2 text-xs text-gray-500 dark:border-gray-700">{c.noDue} việc không đặt hạn chót (tính vào đúng hạn khi xong)</div>}
              </>
            )}
          </Card>
        </div>

        {/* Cần chú ý */}
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
          <Card title="Quá hạn lâu nhất" extra={c.overdue > 5 && <button onClick={() => byBucket('overdue')} className="text-xs font-semibold text-[#3e91ab] hover:underline">Xem tất cả {c.overdue}</button>}>
            {longestOverdue.length === 0 ? <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Không có việc quá hạn" /> : (
              <div className="divide-y divide-gray-100 dark:divide-gray-700">
                {longestOverdue.map(t => (
                  <button key={t.id} onClick={() => navigate(`/work-list?task=${t.id}`)} className="flex w-full items-center justify-between gap-3 py-2 text-left text-sm hover:bg-gray-50 dark:hover:bg-gray-700/50">
                    <span className="min-w-0"><b className="block truncate">{t.name}</b><span className="text-xs text-gray-500">{memberOf(t.assigneeId)?.name ?? 'Chưa giao'} · {projectOf(t.projectId)?.name}</span></span>
                    <span className="shrink-0 text-right text-xs font-semibold text-red-500">trễ {dayjs(now).diff(t.dueAt, 'day')} ngày</span>
                  </button>
                ))}
              </div>
            )}
          </Card>

          <Card title={<Tooltip title="Chỉ tính việc đang mở (đang làm, quá hạn, chờ duyệt)">Ma trận ưu tiên (Eisenhower)</Tooltip>}>
            <div className="grid flex-1 grid-cols-2 gap-2">
              {quadrants.map(({ q, list }) => (
                <button key={q} onClick={() => openDrawer(`${QUADRANT_META[q].label} · ${QUADRANT_META[q].hint}`, list)} disabled={!list.length}
                  className={`flex flex-col items-start rounded-lg border p-3 text-left transition hover:shadow-sm disabled:cursor-default ${QUADRANT_META[q].cls}`}>
                  <span className="text-2xl font-light leading-none">{list.length}</span>
                  <span className="mt-1 text-xs font-bold">{QUADRANT_META[q].label}</span>
                  <span className="text-[11px] opacity-80">{QUADRANT_META[q].hint}</span>
                </button>
              ))}
            </div>
          </Card>

          <Card title="Thành viên làm tốt" extra={<Tooltip title="Xếp theo số việc hoàn thành đúng hạn, tối thiểu 3 việc được giao. % = đúng hạn / số việc đã xong hoặc đã đến hạn"><span className="text-xs text-gray-400">?</span></Tooltip>} className="md:col-span-2 xl:col-span-1">
            {top.length === 0 ? <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Chưa đủ dữ liệu" /> : (
              <div className="space-y-1">
                {top.map((m, i) => (
                  <button key={m.key} onClick={() => openDrawer(m.name, tasksOfGroup(tasks, 'assignee', m.key))} className="flex w-full items-center justify-between gap-3 rounded px-1 py-1.5 text-left hover:bg-gray-50 dark:hover:bg-gray-700/50">
                    <span className="flex min-w-0 items-center gap-2.5">
                      <span className="w-4 text-xs font-bold text-gray-400">{i + 1}</span>
                      <Avatar name={m.name} color={m.color} size={30} />
                      <span className="min-w-0"><b className="block truncate text-[13px]">{m.name}</b><span className="text-xs text-gray-500">{m.on_time} việc đúng hạn · {m.total} được giao</span></span>
                    </span>
                    <span className="shrink-0 rounded bg-green-50 px-2 py-0.5 text-[11px] font-bold text-green-600 dark:bg-green-900/30">{m.rate}%</span>
                  </button>
                ))}
              </div>
            )}
          </Card>
        </div>

        {routines.length > 0 && (
          <Card title={<Tooltip title="Việc lặp lại theo quy trình (khử khuẩn, hộp chống sốc, nhiệt độ tủ thuốc, bảo trì máy…) — tính trên các lần đã đến hạn trong kỳ">Tuân thủ quy trình định kỳ</Tooltip>}
            extra={<button onClick={() => navigate('/work-list?view=repeat')} className="text-xs font-semibold text-[#3e91ab] hover:underline">Mở việc định kỳ</button>}>
            <Table<RoutineRow> rowKey="key" size="middle" dataSource={routines} pagination={false} scroll={{ x: 'max-content' }}
              onRow={r => ({ onClick: () => openDrawer(r.name, r.tasks), className: 'cursor-pointer' })}
              columns={[
                {
                  key: 'name', title: 'Quy trình', render: (_, r) => (
                    <span className="block min-w-[240px]"><b>{r.name}</b>
                      <span className="block text-xs text-slate-500">{departmentName(r.departmentId)}{r.repeat && ` · ${REPEAT_LABEL[r.repeat]}`}</span></span>
                  ),
                },
                { key: 'due', title: 'Lần đến hạn', align: 'right', width: 110, render: (_, r) => r.due },
                { key: 'on', title: 'Đúng hạn', align: 'right', width: 100, render: (_, r) => <span className="font-semibold text-green-600">{r.onTime}</span> },
                { key: 'late', title: 'Muộn', align: 'right', width: 80, render: (_, r) => (r.late ? <span className="font-semibold text-amber-600">{r.late}</span> : <span className="text-gray-300">0</span>) },
                { key: 'miss', title: <Tooltip title="Quá hạn chưa làm hoặc đóng không đạt">Bỏ lỡ</Tooltip>, align: 'right', width: 80, render: (_, r) => (r.missed ? <span className="font-semibold text-red-500">{r.missed}</span> : <span className="text-gray-300">0</span>) },
                {
                  key: 'rate', title: '% tuân thủ', align: 'right', width: 110, render: (_, r) => (r.rate === null ? <span className="text-xs text-slate-400">Chưa đến hạn</span> : (
                    <span className={`rounded px-2 py-0.5 text-xs font-bold ${r.rate >= 95 ? 'bg-green-50 text-green-700' : r.rate >= 80 ? 'bg-amber-50 text-amber-700' : 'bg-red-50 text-red-600'}`}>{r.rate}%</span>
                  )),
                },
              ]} />
          </Card>
        )}

        <Card title="Theo dự án & phòng ban"
          extra={(
            <span className="flex items-center gap-2">
              <Segmented size="small" value={projBy} onChange={v => setProjBy(v as typeof projBy)} options={[{ value: 'project', label: 'Dự án' }, { value: 'department', label: 'Phòng ban' }]} />
              <Button size="small" icon={<Download size={13} />} disabled={!projRows.length} onClick={() => exportExcel(`bao-cao-${projBy === 'project' ? 'du-an' : 'phong-ban'}-${now}.xlsx`, groupExport(projRows, projBy))}>Xuất Excel</Button>
            </span>
          )}>
          <GroupTable rows={projRows} tasks={tasks} by={projBy} onOpen={openDrawer} />
        </Card>

        <Card title="Theo thành viên"
          extra={(
            <span className="flex items-center gap-2">
              <Segmented size="small" value={memberBy} onChange={v => setMemberBy(v as typeof memberBy)} options={[{ value: 'assignee', label: 'Người thực hiện' }, { value: 'creator', label: 'Người tạo / giao việc' }]} />
              <Button size="small" icon={<Download size={13} />} disabled={!memberRows.length} onClick={() => exportExcel(`bao-cao-thanh-vien-${now}.xlsx`, groupExport(memberRows, memberBy))}>Xuất Excel</Button>
            </span>
          )}>
          <GroupTable rows={memberRows} tasks={tasks} by={memberBy} onOpen={openDrawer} />
        </Card>
      </div>
      <TaskDrawer state={drawer} onClose={() => setDrawer(null)} />
    </Layout>
  );
}
